import { useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Text } from '@/components/ui/core';
import { Stack, Center } from '@/components/ui/layout';
import { Loader } from '@/components/ui/feedback';
import { useVendorContext } from '../../hooks/useVendorContext';
import { useEnrichedAuth } from '../../hooks/useEnrichedAuth';
import { usePreferencesStore } from '../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../hooks/useAccessibilityStyles';

interface VendorGuardProps {
  children: ReactNode;
  /** Redirect path when vendor context is not available */
  redirectTo?: string;
  /** Show loading state while fetching vendor context */
  showLoading?: boolean;
  /** Loading message to display */
  loadingMessage?: string;
  /** Fallback component to render while loading */
  fallback?: ReactNode;
}

/**
 * VendorGuard Component
 *
 * Route guard that ensures vendor context is loaded before rendering children.
 * Redirects to registration if user has no vendor.
 *
 * Usage:
 * ```tsx
 * <VendorGuard>
 *   <DashboardPage />
 * </VendorGuard>
 * ```
 */
export default function VendorGuard({
  children,
  redirectTo = '/auth/vendor-register',
  showLoading = true,
  loadingMessage = 'Loading vendor information...',
  fallback,
}: VendorGuardProps): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: isAuthLoading } = useEnrichedAuth();
  const { hasVendor, isLoading, isInitialized, error } = useVendorContext();
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [isAuthLoading, isAuthenticated, navigate, location.pathname]);

  // Redirect to registration if no vendor found after initialization.
  // All recovery logic (context endpoint, summary fallback) is handled by
  // useVendorContext's fetchVendorContext fallback chain — the guard simply
  // trusts isInitialized and hasVendor.
  useEffect(() => {
    if (isInitialized && !hasVendor && !isLoading) {
      console.warn('[VendorGuard] No vendor found after initialization. Redirecting to registration.');
      navigate(redirectTo, { state: { from: location.pathname } });
    }
  }, [isInitialized, hasVendor, isLoading, navigate, redirectTo, location.pathname]);

  // Still loading auth
  if (isAuthLoading) {
    if (!showLoading) return null;
    if (fallback) return <>{fallback}</>;

    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed" style={accessibilityStyles}>Checking authentication...</Text>
        </Stack>
      </Center>
    );
  }

  // Not authenticated - redirect handled by useEffect
  if (!isAuthenticated) {
    return null;
  }

  // Loading vendor context
  if (isLoading || !isInitialized) {
    if (!showLoading) return null;
    if (fallback) return <>{fallback}</>;

    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed" style={accessibilityStyles}>{loadingMessage}</Text>
        </Stack>
      </Center>
    );
  }

  // Error loading vendor context (but has vendor - show error)
  if (error && hasVendor) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Text c="red" fw={500} style={accessibilityStyles}>
            Error loading vendor information
          </Text>
          <Text c="dimmed" size="sm" style={accessibilityStyles}>
            {error}
          </Text>
        </Stack>
      </Center>
    );
  }

  // No vendor - redirect handled by useEffect
  if (!hasVendor) {
    return null;
  }

  // Vendor context loaded - render children
  return <>{children}</>;
}
